import { useEffect, useState } from "react";
import { Check, Copy, KeyRound, TriangleAlert, X } from "lucide-react";
import { THEME } from "@/config/theme";
import SetupSnippet from "./SetupSnippet";

interface Props {
  token: string;
  name: string;
  onClose: () => void;
}

/**
 * Shown once, immediately after a key is minted. The server stores only a
 * SHA-256 hash, so this dialog is the single moment the plaintext exists in the
 * UI — hence the warning, the copy button, and the ready-to-paste snippet with
 * the real token already substituted.
 *
 * Deliberately not ConfirmDialog: that one takes a fixed message string, is
 * hardwired to a red destructive treatment, and is 320px wide — too narrow for
 * a 68-char token plus a multi-line snippet.
 */
export default function TokenDialog({ token, name, onClose }: Props) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Non-secure origin or denied permission — the token is selectable.
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="token-dialog-title"
        className={`${THEME.panel.bg} ${THEME.panel.border} ${THEME.scrollbar.dark} rounded-xl shadow-2xl p-5 w-full max-w-[560px] flex flex-col gap-4 max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2">
          <div className="flex items-center gap-2 text-emerald-400 flex-1 min-w-0">
            <KeyRound size={18} className="shrink-0" />
            <span id="token-dialog-title" className={`${THEME.mcpPage.dialogTitle} truncate`}>
              Token created — {name}
            </span>
          </div>
          {/* Escape and the backdrop already close this, but neither is
              discoverable — without a visible affordance the dialog reads as
              modal-until-Done. */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`shrink-0 p-1 -m-1 rounded-md ${THEME.button.ghost} transition-colors`}
          >
            <X size={18} />
          </button>
        </div>

        <div className="relative">
          <pre
            data-testid="raw-token"
            className={`${THEME.pageCard.snippetBlock} ${THEME.mcpPage.code} rounded-lg p-3.5 pr-11 break-all whitespace-pre-wrap leading-relaxed`}
          >
            {token}
          </pre>
          <button
            type="button"
            onClick={copy}
            aria-label="Copy token"
            className={`absolute top-2 right-2 p-1.5 rounded-md ${THEME.button.ghost} transition-colors`}
          >
            {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
          </button>
        </div>

        <div className="flex items-start gap-2 text-amber-500">
          <TriangleAlert size={15} className="mt-0.5 shrink-0" />
          <p className={`${THEME.mcpPage.control} leading-snug`}>
            Copy it now — this is the only time it will be shown. We store a one-way
            hash, so it cannot be recovered. If you lose it, revoke this key and
            create another.
          </p>
        </div>

        <div className={`h-px ${THEME.panel.border} border-t`} />

        <SetupSnippet token={token} />

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className={`px-3.5 py-2 ${THEME.mcpPage.control} rounded-lg ${THEME.button.primary} transition-colors`}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
